import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { WalletSelfDiscoveryBoard } from "./WalletSelfDiscoveryBoard";

type AssessmentRow={
  administrationId:string;status:string;assignedAt:string;startedAt?:string|null;completedAt?:string|null;
  versionId:string;title:string;description?:string|null;estimatedMinutes?:number|null;
  instrumentName:string;versionLabel:string;resultCount:number;
};

type FormData={
  administration:{administrationId:string;status:string;assignedAt:string;startedAt?:string|null;completedAt?:string|null};
  assessment:{title:string;description?:string|null;estimatedMinutes?:number|null;instrumentName:string;versionLabel:string};
  items:Array<{
    itemVersionId:string;prompt:string;instruction?:string|null;orderNo:number;required:boolean;
    options:Array<{valueKey:string;label:string;orderNo:number}>;
    response?:{valueKey?:string}|null;
  }>;
};

type ResultData={
  administrationId:string;status:string;completedAt:string;title:string;instrumentName:string;
  results:Array<{
    resultId:string;targetType:string;targetRef?:string|null;targetName:string;
    rawScore:number;normalizedScore?:number|null;percentile?:number|null;
    interpretation?:{label:string;summary:string;recommendation?:string|null}|null;
  }>;
};

function statusLabel(status:string){
  if(status==="ASSIGNED")return "시작 전";
  if(status==="IN_PROGRESS")return "진행 중";
  if(status==="COMPLETED")return "완료";
  if(status==="CANCELLED")return "취소";
  return status;
}

export function WalletAssessmentsPage({
  client,
  onNavigate,
  onUseInPortfolio
}:{
  client:PlatformClient;
  onNavigate:(path:string)=>void;
  onUseInPortfolio:(resultId:string)=>void;
}){
  const [items,setItems]=useState<AssessmentRow[]>([]);
  const [selectedId,setSelectedId]=useState("");
  const [form,setForm]=useState<FormData|null>(null);
  const [result,setResult]=useState<ResultData|null>(null);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [mode,setMode]=useState<"ASSESSMENTS"|"INSIGHTS">("ASSESSMENTS");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_assessments").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const rows=(data || []) as AssessmentRow[];
      setItems(rows);
      setSelectedId((v)=>rows.some((x)=>x.administrationId===v)?v:rows[0]?.administrationId || "");
    });
    return()=>{active=false;};
  },[client,refresh]);

  const selected=useMemo(()=>items.find((x)=>x.administrationId===selectedId)||null,[items,selectedId]);

  useEffect(()=>{
    if(!selectedId){setForm(null);setResult(null);return;}
    let active=true;
    const load=selected?.status==="COMPLETED"
      ? client.rpc("wallet_assessment_result",{p_administration_id:selectedId})
      : client.rpc("wallet_assessment_form",{p_administration_id:selectedId});
    load.then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      if(selected?.status==="COMPLETED"){setResult(data as ResultData);setForm(null);}
      else{setForm(data as FormData);setResult(null);}
    });
    return()=>{active=false;};
  },[client,selectedId,selected?.status,refresh]);

  async function answer(itemVersionId:string,valueKey:string){
    setBusy(itemVersionId);setError("");
    const {error}=await client.rpc("wallet_save_assessment_response",{p_administration_id:selectedId,p_item_version_id:itemVersionId,p_value_key:valueKey});
    setBusy("");
    if(error){setError(error.message);return;}
    setForm((current)=>current?{
      ...current,
      administration:{...current.administration,status:"IN_PROGRESS"},
      items:current.items.map((item)=>item.itemVersionId===itemVersionId?{...item,response:{valueKey}}:item)
    }:current);
    setItems((current)=>current.map((item)=>item.administrationId===selectedId?{...item,status:"IN_PROGRESS"}:item));
  }

  async function complete(){
    setBusy("complete");setError("");
    const {data,error}=await client.rpc("wallet_complete_assessment",{p_administration_id:selectedId});
    setBusy("");
    if(error){setError(error.message);return;}
    setResult(data as ResultData);
    setForm(null);
    setRefresh((v)=>v+1);
  }

  const allRequiredAnswered=form?.items.every((item)=>!item.required || Boolean(item.response?.valueKey)) ?? false;

  return (
    <section className="wallet-page">
      <div className="section-heading">
        <div><p className="eyebrow">Assessment</p><h2>나의 검사</h2><p className="route-description">학교가 배정한 발행·검증된 검사만 실시합니다. 원본 응답은 검사 영역에 격리됩니다.</p></div>
        <span className="phase-badge">BUILD-08</span>
      </div>
      <div className="growth-mode-switch" role="tablist" aria-label="검사 보기 방식">
        <button className={mode==="ASSESSMENTS"?"active":""} onClick={()=>setMode("ASSESSMENTS")}>검사하기</button>
        <button className={mode==="INSIGHTS"?"active":""} onClick={()=>setMode("INSIGHTS")}>자기이해</button>
      </div>

      {error && <p className="error">{error}</p>}

      {mode==="INSIGHTS" ? (
        <WalletSelfDiscoveryBoard
          client={client}
          onNavigate={onNavigate}
          onUseInPortfolio={onUseInPortfolio}
        />
      ) : (
      <div className="wallet-assessment-layout">
        <aside className="wallet-assessment-list">
          <div className="panel-title"><strong>검사</strong><span>{items.length}건</span></div>
          {items.map((item)=>(
            <button key={item.administrationId} className={selectedId===item.administrationId?"wallet-assessment-row active":"wallet-assessment-row"} onClick={()=>setSelectedId(item.administrationId)}>
              <strong>{item.title}</strong><span>{item.instrumentName}</span><small>{statusLabel(item.status)} · 약 {item.estimatedMinutes || "-"}분</small>
            </button>
          ))}
          {!items.length && <p className="case-empty">배정된 검사가 없습니다.</p>}
        </aside>

        <div className="wallet-assessment-detail">
          {form && (
            <>
              <header className="wallet-assessment-head"><div><p className="eyebrow">{form.assessment.instrumentName} · v{form.assessment.versionLabel}</p><h2>{form.assessment.title}</h2><p>{form.assessment.description}</p></div><span className="status-pill">{statusLabel(form.administration.status)}</span></header>
              <div className="assessment-question-list">
                {form.items.map((item)=>(
                  <article className="assessment-question" key={item.itemVersionId}>
                    <div className="question-copy"><span>{item.orderNo}</span><strong>{item.prompt}</strong>{item.required && <small>필수</small>}</div>
                    <div className="question-options">
                      {item.options.map((option)=>(
                        <button key={option.valueKey} className={item.response?.valueKey===option.valueKey?"option-button selected":"option-button"} disabled={busy===item.itemVersionId} onClick={()=>answer(item.itemVersionId,option.valueKey)}>{option.label}</button>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
              <div className="assessment-complete-bar"><span>{form.items.filter((i)=>i.response?.valueKey).length}/{form.items.length} 응답</span><button className="primary compact-button" disabled={busy!==""||!allRequiredAnswered} onClick={complete}>{busy==="complete"?"채점 중…":"검사 완료·결과 보기"}</button></div>
            </>
          )}

          {result && (
            <>
              <header className="wallet-assessment-head"><div><p className="eyebrow">{result.instrumentName}</p><h2>{result.title}</h2><p>검사가 완료되었습니다. 아래 결과는 자기이해를 돕는 자료이며 실제 활동과 함께 해석하세요.</p></div><span className="status-pill good">완료</span></header>
              <div className="assessment-result-grid">
                {result.results.map((r)=>(
                  <article className="assessment-result-card" key={r.resultId}>
                    <div className="result-score"><span>{r.targetName}</span><strong>{r.normalizedScore ?? Number(r.rawScore).toFixed(2)}</strong><small>{r.normalizedScore!=null?"정규화 점수":"원점수"}{r.percentile!=null?" · 백분위 "+r.percentile:""}</small></div>
                    {r.interpretation ? <div className="result-interpretation"><h3>{r.interpretation.label}</h3><p>{r.interpretation.summary}</p>{r.interpretation.recommendation && <small>{r.interpretation.recommendation}</small>}</div> : <p className="muted">이 점수 구간에 공개 가능한 해석 규칙이 없습니다.</p>}
                  </article>
                ))}
              </div>
              <div className="assessment-human-gate"><strong>Career Journey 연결은 사람의 확인을 거칩니다.</strong><p>검사에서 파생된 결과가 역량과 연결된 경우에도 교사가 Evidence를 검증하고 평가를 확정하기 전에는 CompetencyResult가 생성되지 않습니다.</p></div>
            </>
          )}

          {!form && !result && selectedId && <p className="muted">검사 정보를 불러오는 중…</p>}
        </div>
      </div>
      )}
    </section>
  );
}
